import { Location } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { addQueryStringParameter, Guid } from "moo-shared";
import { Observable } from "rxjs";
import { IPasswordPolicyDto } from "../../features/password-update/password-update.service";
import { IQrCodeValidatedDto, ISecretDto } from "./authentication-api.service";
import { OidcService } from "./oidc.service";

@Injectable()
export class AuthenticationServerService {

  constructor(
    private readonly httpClient: HttpClient,
    private readonly location: Location,
    private readonly oidcService: OidcService,
  ) {
  }

  authenticateUsingUsernameAndPassword(username: string, password: string): Observable<string> {
    const requestUrl = this.getCredentialsValidationUrl(username);
    return this.httpClient.post<string>(requestUrl, { password: password }, { withCredentials: true });
  }

  authenticateUsingPictures(username: string, keys: string[]): Observable<string> {
    const requestUrl = this.getCredentialsValidationUrl(username);
    const secret = { PictureKeys: keys } as ISecretDto;
    return this.httpClient.post<string>(requestUrl, secret, { withCredentials: true });
  }

  authenticateUsingQrCode(qrCodeKey: Guid, qrCodeVersionKey: Guid): Observable<any | IQrCodeValidatedDto> {
    const requestUrl = this.getQrCredentialsValidationUrl();
    const secret = { QrCodeKey: qrCodeKey, QrCodeVersionKey: qrCodeVersionKey } as ISecretDto;
    return this.httpClient.post<string | IQrCodeValidatedDto>(requestUrl, secret, { withCredentials: true });
  }

  authenticateUsingQrCodeAndPictures(userName: string, qrCodeKey: Guid, qrCodeVersionKey: Guid, pictureKeys: Guid[]): Observable<string> {
    const requestUrl = this.getCredentialsValidationUrl(userName);
    const secret = { QrCodeKey: qrCodeKey, QrCodeVersionKey: qrCodeVersionKey, PictureKeys: pictureKeys } as ISecretDto;
    return this.httpClient.post<string>(requestUrl, secret, { withCredentials: true });
  }

  getPasswordPolicy(loginName: string): Observable<IPasswordPolicyDto> {
    const requestUrl = this.location.prepareExternalUrl(`/accounts/${encodeURIComponent(loginName)}/userPasswordPolicies`);
    return this.httpClient.get<IPasswordPolicyDto>(requestUrl, { withCredentials: true });
  }

  private getCredentialsValidationUrl(username: string): string {
    const encodedUserName = encodeURIComponent(username);
    const requestUrl = this.location.prepareExternalUrl(`/account/${encodedUserName}/validateCredentials`);
    return this.appendCookieParamIfNeeded(requestUrl);
  }

  private getQrCredentialsValidationUrl(): string {
    const requestUrl = this.location.prepareExternalUrl("/validateCredentials");
    return this.appendCookieParamIfNeeded(requestUrl);
  }

  private appendCookieParamIfNeeded(requestUrl: string): string {
    if (this.oidcService.isClientConfigAvailable) {
      requestUrl = addQueryStringParameter(requestUrl, "setCookie", true);
      return addQueryStringParameter(requestUrl, this.oidcService.openIdClientString, null);
    }
    return requestUrl;
  }
}

export enum LoginType {
  Text = "Text",
  Pictures = "Pictures",
  QrCode = "QrCode",
  QrCodeAndPictures = "QrCodeAndPictures"
}
